import { useEffect, useState } from "react";
import api from "../../../../../Axios.js";
import { toast } from "react-toastify";
import useConfirm from "../../../../../hooks/useConfirm.js";
import { useNavigate } from "react-router-dom";
import { cleanHtml } from "../../../../../Helper/functionHelper.js";

export default function Step5Review({ programId, initialData, setMasterRender, prev }) {

  const navigate = useNavigate();
  const [program, setProgram] = useState(initialData || null);
  const [loading, setLoading] = useState(false);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/lnd/${programId}/preview`);
      setProgram(res.data?.data?.program);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load program preview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialData && programId) fetchPreview();
    else if (initialData) setProgram(initialData);
  }, [programId, initialData]);

  const { confirm } = useConfirm();
  const publish = async () => {
    try {

      const isConfirmed = await confirm({
        title: "Confirm Program Publish",
        content: `Are you sure you want to publish this program(You are not able to edit it after publishing)?`,
        confirmText: "Yes, Publish",
        cancelText: "Cancel"
      });
      if (!isConfirmed) return;

      await api.post(`/lnd/${programId}/publish`);
      setMasterRender(prev => !prev);
      toast.success("Program published successfully");
      navigate(-1)
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to publish program");
    }
  };

  const draft = async () => {
    try {
      toast.success("Program drafted successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to draft program");
    }
  };

  const stripHtml = (html) => {
    if (!html) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  if (loading) return <p>Loading preview...</p>;
  if (!program) return null;

  return (
    <div className="container-fluid">

      <h5 className="mb-4 fw-bold text-dark">Review & Publish</h5>

      {/* BASIC INFO */}
      <div className="card shadow-sm border-0 mb-4 p-4 rounded-3">
        <h6 className="mb-4 fw-bold text-primary">Basic Information</h6>

        <div className="row mb-2">
          <div className="col-md-6">
            <strong>Name:</strong> {program.program_name}
          </div>
          <div className="col-md-6">
            <strong>Code:</strong> {program.program_code}
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-md-6">
            <strong>Mode:</strong> {program.learning_mode}
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <strong>Description:</strong>
            <div>{stripHtml(program.description)}</div>
          </div>
        </div>
      </div>

      {/* SCHEDULE */}
      <div className="card shadow-sm border-0 mb-4 p-4 rounded-3">
        <h6 className="mb-4 fw-bold text-primary">Schedule</h6>

        <div className="row mb-2">
          <div className="col-md-4">
            <strong>Duration:</strong> {program.duration_hours} hours
          </div>
          <div className="col-md-4">
            <strong>Cost:</strong> ₹{program.estimated_cost}
          </div>
          <div className="col-md-4">
            <strong>Certification:</strong>{" "}
            {program.is_certified ? "Yes" : "No"}
          </div>
        </div>

      </div>

      {/* MODULES */}
      <div className="card shadow-sm border-0 mb-4 p-4 rounded-3">
        <h6 className="mb-4 fw-bold text-primary">Modules</h6>

        {program.modules?.length > 0 ? (
          <div className="accordion" id="modulesAccordion">
            {program.modules.map((m, i) => (
              <div className="accordion-item" key={i}>
                <h2 className="accordion-header" id={`heading${i}`}>
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse${i}`}
                    aria-expanded="false"
                    aria-controls={`collapse${i}`}
                  >
                    {i + 1}. {m.title}
                  </button>
                </h2>

                <div
                  id={`collapse${i}`}
                  className="accordion-collapse collapse"
                  aria-labelledby={`heading${i}`}
                  data-bs-parent="#modulesAccordion"
                >
                  <div className="accordion-body">
                    <div dangerouslySetInnerHTML={{ __html: cleanHtml(m.description) }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted">No modules added</p>
        )}
      </div>

      {/* SKILLS */}
      <div className="card shadow-sm border-0 mb-4 p-4 rounded-3">
        <h6 className="mb-4 fw-bold text-primary">Skill Outcomes</h6>

        {program.skill_outcomes?.length > 0 ? (
          <ul className="list-group">
            {program.skill_outcomes.map((s, i) => (
              <li key={i} className="list-group-item">
                <div className="row">
                  <div className="col-md-6">
                    <strong>Skill:</strong>{" "}
                    {s?.skill?._Stitle ? s?.skill?._Stitle : "N/A"}
                  </div>
                  <div className="col-md-3">
                    <strong>Skill Level:</strong> {s.target_level}
                  </div>
                  <div className="col-md-3">
                    <strong>Proficiency Level:</strong> {s.rating_gain}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted">No skills added</p>
        )}
      </div>

      {/* ACTIONS */}
      <div className="d-flex justify-content-between mt-4 px-2">
        <button className="btn btn-secondary px-4 fw-medium" onClick={prev}>
          Previous
        </button>

        <div className="d-flex gap-3">
          {program.status !== "published" && (
            <button className="btn btn-secondary px-4 fw-medium" onClick={draft}>
              Save As Draft
            </button>
          )}

          {program.status !== "published" ? (
            <button className="btn btn-success px-4 fw-medium" onClick={publish}>
              Publish Program
            </button>
          ) : (
            <button className="btn btn-success px-4 fw-medium disabled">
              Published
            </button>
          )}
        </div>
      </div>

    </div>
  );
}